import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Contrast, RotateCcw, SunMoon, X, ZoomIn, ZoomOut } from "lucide-react";

/*
 * Full-screen image viewer, ported from the CDMQB ImageZoomModal
 * (no media gallery / thumbnails). Zoom with buttons, mouse wheel or pinch;
 * drag to pan; optional invert and contrast filters.
 */

type ImageZoomModalProps = {
  imageSource: string | null;
  isOpen: boolean;
  onClose: () => void;
  altText?: string;
};

const roundButton: React.CSSProperties = {
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  color: "white",
  padding: "8px",
  borderRadius: "50%",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background-color 0.2s",
};

const pillButton = (active: boolean): React.CSSProperties => ({
  backgroundColor: active
    ? "rgba(255, 255, 255, 0.4)"
    : "rgba(255, 255, 255, 0.2)",
  color: "white",
  padding: "8px 12px",
  borderRadius: "20px",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "12px",
  transition: "background-color 0.2s",
});

const hoverOn = (e: React.MouseEvent<HTMLButtonElement>) =>
  (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.3)");
const hoverOff = (e: React.MouseEvent<HTMLButtonElement>) =>
  (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)");

const ImageZoomModal = ({
  imageSource,
  isOpen,
  onClose,
  altText = "Question Image",
}: ImageZoomModalProps) => {
  const [scale, setScale] = useState(1);
  const [initialScale, setInitialScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastTouch, setLastTouch] = useState({ x: 0, y: 0 });
  const [lastDistance, setLastDistance] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [invertValue, setInvertValue] = useState(100);
  const [invertEnabled, setInvertEnabled] = useState(false);
  const [contrastValue, setContrastValue] = useState(0);
  const [contrastEnabled, setContrastEnabled] = useState(false);

  // Reset everything each time the viewer opens (layout effect so it runs
  // before the image's load event can fire and never hides a loaded image)
  useLayoutEffect(() => {
    if (isOpen) {
      setImageLoaded(false);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
      setInvertValue(100);
      setInvertEnabled(false);
      setContrastValue(0);
      setContrastEnabled(false);
    }
  }, [isOpen, imageSource]);

  // Lock page scroll while open, close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  const calculateFitScale = useCallback(() => {
    const img = imageRef.current;
    if (!img || !isOpen) return;
    const { naturalWidth, naturalHeight } = img;
    if (naturalWidth === 0 || naturalHeight === 0) return;

    const fitScale = Math.min(
      (window.innerWidth * 0.9) / naturalWidth,
      (window.innerHeight * 0.85) / naturalHeight,
      1,
    );
    setInitialScale(fitScale);
    setScale(fitScale);
  }, [isOpen]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    calculateFitScale();
  };

  // Safety net: an image that was already decoded may finish loading before
  // React attaches onLoad, so check for that directly once it's on screen.
  useEffect(() => {
    const img = imageRef.current;
    if (isOpen && img && img.complete && img.naturalWidth > 0) {
      handleImageLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, imageSource]);

  const minScale = Math.min(initialScale, 0.2);
  const clampScale = (value: number) => Math.max(minScale, Math.min(5, value));

  const handleZoomIn = () => setScale((prev) => Math.min(prev * 1.5, 5));
  const handleZoomOut = () =>
    setScale((prev) => Math.max(prev / 1.5, minScale));

  const handleReset = () => {
    setScale(initialScale);
    setPosition({ x: 0, y: 0 });
    setInvertValue(100);
    setInvertEnabled(false);
    setContrastValue(0);
    setContrastEnabled(false);
  };

  const getImageFilter = () => {
    const filters: string[] = [];
    if (invertEnabled) filters.push(`invert(${invertValue}%)`);
    if (contrastEnabled) filters.push(`contrast(${100 + contrastValue}%)`);
    return filters.length > 0 ? filters.join(" ") : "none";
  };

  const getDistance = (t1: Touch, t2: Touch) =>
    Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  // Touch: one finger pans, two fingers pinch-zoom. Attached natively so
  // preventDefault works (React's touch listeners are passive).
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isOpen) return;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        setIsDragging(false);
        setLastDistance(getDistance(e.touches[0], e.touches[1]));
      } else if (e.touches.length === 1) {
        setIsDragging(true);
        setLastTouch({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 1 && isDragging) {
        const dx = e.touches[0].clientX - lastTouch.x;
        const dy = e.touches[0].clientY - lastTouch.y;
        setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
        setLastTouch({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      } else if (e.touches.length === 2 && lastDistance > 0) {
        const distance = getDistance(e.touches[0], e.touches[1]);
        setScale((prev) => clampScale(prev * (distance / lastDistance)));
        setLastDistance(distance);
      }
    };

    const onTouchEnd = () => setIsDragging(false);

    container.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);
    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDragging, lastTouch, lastDistance, initialScale]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setLastTouch({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const dx = e.clientX - lastTouch.x;
    const dy = e.clientY - lastTouch.y;
    setPosition((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    setLastTouch({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleWheel = (e: React.WheelEvent) => {
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((prev) => clampScale(prev * delta));
  };

  if (!isOpen || !imageSource || typeof document === "undefined") return null;

  const isNarrow = window.innerWidth < 768;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={altText}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10100,
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: "normal",
      }}
    >
      {/* Top toolbar */}
      <div
        style={{
          position: "absolute",
          top: "16px",
          left: "16px",
          right: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={handleZoomIn}
            style={roundButton}
            onMouseEnter={hoverOn}
            onMouseLeave={hoverOff}
            aria-label="Zoom in"
          >
            <ZoomIn size={20} />
          </button>
          <button
            type="button"
            onClick={handleZoomOut}
            style={roundButton}
            onMouseEnter={hoverOn}
            onMouseLeave={hoverOff}
            aria-label="Zoom out"
          >
            <ZoomOut size={20} />
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={roundButton}
            onMouseEnter={hoverOn}
            onMouseLeave={hoverOff}
            aria-label="Reset zoom and position"
          >
            <RotateCcw size={20} />
          </button>

          <div
            style={{
              width: "1px",
              height: "24px",
              backgroundColor: "rgba(255,255,255,0.3)",
              margin: "0 4px",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <button
              type="button"
              onClick={() => setInvertEnabled(!invertEnabled)}
              style={pillButton(invertEnabled)}
              aria-pressed={invertEnabled}
              aria-label="Toggle invert"
            >
              <SunMoon size={16} />
              Invert
            </button>
            {invertEnabled && (
              <input
                type="range"
                min="0"
                max="100"
                value={invertValue}
                onChange={(e) => setInvertValue(Number(e.target.value))}
                style={{ width: "80px", cursor: "pointer" }}
                aria-label="Invert amount"
              />
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <button
              type="button"
              onClick={() => setContrastEnabled(!contrastEnabled)}
              style={pillButton(contrastEnabled)}
              aria-pressed={contrastEnabled}
              aria-label="Toggle contrast"
            >
              <Contrast size={16} />
              Contrast
            </button>
            {contrastEnabled && (
              <input
                type="range"
                min="-100"
                max="100"
                value={contrastValue}
                onChange={(e) => setContrastValue(Number(e.target.value))}
                style={{ width: "80px", cursor: "pointer" }}
                aria-label="Contrast amount"
              />
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          style={roundButton}
          onMouseEnter={hoverOn}
          onMouseLeave={hoverOff}
          aria-label="Close image"
        >
          <X size={24} />
        </button>
      </div>

      {/* Zoom level */}
      <div
        style={{
          position: "absolute",
          top: "80px",
          left: "16px",
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          color: "white",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "14px",
          zIndex: 10,
        }}
      >
        {Math.round((scale / initialScale) * 100)}% {scale < 1 && "(fit)"}
      </div>

      {/* Image stage */}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          cursor: isDragging ? "grabbing" : "grab",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <img
          key={imageSource}
          ref={imageRef}
          src={imageSource}
          alt={altText}
          onLoad={handleImageLoad}
          draggable={false}
          style={{
            maxWidth: "none",
            maxHeight: "none",
            userSelect: "none",
            pointerEvents: "none",
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isDragging ? "none" : "transform 0.1s ease-out",
            filter: getImageFilter(),
            opacity: imageLoaded ? 1 : 0,
          }}
        />
      </div>

      {/* Hint */}
      <div
        style={{
          position: "absolute",
          bottom: "16px",
          left: "16px",
          right: "16px",
          textAlign: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "inline-block",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            color: "white",
            padding: "8px 16px",
            borderRadius: "8px",
            fontSize: "14px",
          }}
        >
          {isNarrow
            ? "Pinch to zoom • Drag to pan"
            : "Scroll to zoom • Drag to pan • Use buttons above"}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ImageZoomModal;
